import urlcat from "urlcat";

import { axiosInstance, parseResponse, TENANT_API_URL } from "../..";

const SCHEDULE_VISIT = `${TENANT_API_URL}/service-users/schedule-visit`;

// create schedule visit api
export const createScheduleVisitAsync = (data: Record<string, unknown>) =>
  axiosInstance.post(`${SCHEDULE_VISIT}`, data);

export const createScheduleVisit = async (data: Record<string, unknown>) => {
  try {
    const response = await axiosInstance.post(`${SCHEDULE_VISIT}`, data);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const getAllScheduleVisitsAsync = (
  {
    startDate,
    endDate,
    filterInactivityVisits,
  }: { startDate: string; endDate: string; filterInactivityVisits?: boolean },
  signal?: AbortSignal
) =>
  axiosInstance.get(SCHEDULE_VISIT, {
    params: {
      startDate,
      endDate,
      filterInactivityVisits,
    },
    signal,
  });

export const getScheduleVisitsByOriginAsync = ({
  id,
  startDate,
  endDate,
}: {
  id: string;
  startDate?: string | Date;
  endDate?: string | Date;
}) => {
  if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
    throw new Error("Invalid ID");
  }
  return axiosInstance.get(`${SCHEDULE_VISIT}/origin/${id}`, {
    params: {
      startDate,
      endDate,
    },
  });
};

export const getAllScheduleVisits = async () => {
  try {
    const response = await axiosInstance.get(SCHEDULE_VISIT);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const getScheduleVisitsByUser = async (
  data: Record<string, unknown>
) => {
  try {
    const url = urlcat(`${SCHEDULE_VISIT}/:id`, data);
    const response = await axiosInstance.get(url);
    return parseResponse(response);
  } catch (error: any) {
    // The API historically used 404 for an empty service-user/date range.
    // Treat that response as an empty result so callers do not retain stale
    // rows from the previously selected service user.
    if (error?.response?.status === 404) {
      return {
        success: true,
        data: [],
        formattedScheduleVisit: {},
        message: "No schedule visits found",
      };
    }
    return parseResponse(error.response);
  }
};

//  get schedule visit api by id

export const getSheduleVisitById = async (id: string) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    const response = await axiosInstance.get(`${SCHEDULE_VISIT}/single/${id}`);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const getScheduleVisitAuditTrail = async (
  id: string,
  params?: Record<string, unknown>
) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    const response = await axiosInstance.get(`${SCHEDULE_VISIT}/audit/${id}`, {
      params,
    });
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const getLinkedScheduleVisitAuditHistory = async (
  id: string,
  params?: Record<string, unknown>
) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    const response = await axiosInstance.get(
      `${SCHEDULE_VISIT}/audit-history/${id}`,
      { params }
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const searchLinkedScheduleVisitAuditHistory = async (
  params: Record<string, unknown> = {}
) => {
  try {
    const { serviceUser, staff, ...rest } = params;
    const searchParams = {
      ...rest,
      ...(serviceUser ? { serviceUserId: serviceUser } : {}),
      ...(staff ? { staffId: staff } : {}),
    };
    const response = await axiosInstance.get(`${SCHEDULE_VISIT}/audit-search`, {
      params: searchParams,
    });
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

// update schedule visit api
export const updateScheduleVisitAsync = (
  id: string,
  data: Record<string, unknown>
) => {
  if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
    throw new Error("Invalid ID");
  }
  return axiosInstance.put(`${SCHEDULE_VISIT}/${id}`, data);
};

// update multiple schedule visit api
export const updateMultipleScheduleVisitAsync = (
  data: Record<string, unknown>
) => {
  return axiosInstance.put(
    `${TENANT_API_URL}/service-users/multiple/schedule-visit`,
    data
  );
};

export const updateScheduleVisit = async (
  id: string,
  data: Record<string, unknown>
) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    const response = await axiosInstance.put(`${SCHEDULE_VISIT}/${id}`, data);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

// dlete schedule visit api
export const deleteScheduleVisitAsync = (id: string) => {
  if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
    throw new Error("Invalid ID");
  }
  return axiosInstance.delete(`${SCHEDULE_VISIT}/${id}`);
};

export const deleteScheduleVisit = async (id: string) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    const response = await axiosInstance.delete(`${SCHEDULE_VISIT}/${id}`);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const deleteManyScheduleVisitAsync = async (data: { ids: string[] }) => {
  const { ids } = data;

  if (
    !Array.isArray(ids) ||
    ids.length === 0 ||
    ids.some((id) => !id || !/^[a-zA-Z0-9_-]+$/.test(id))
  ) {
    throw new Error("Invalid IDS");
  }

  try {
    const response = await axiosInstance.delete(
      `${TENANT_API_URL}/service-users/multiple/schedule-visit`,
      { data }
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};
